import { useState, useEffect } from 'react';

export const THEME = {
  LIGHT: '☀️',
  DARK: '🌑',
  SYSTEM: '💻',
} as const;

export type ThemePreference = (typeof THEME)[keyof typeof THEME];

export type ResolvedTheme = 'banenor-light' | 'banenor-dark';

/**
 * Priority:
 * 1. Explicit data-theme attribute on <html> (set by ThemeSwitcher)
 * 2. Saved preference in localStorage key 'theme' (fallback for system default)
 * 3. OS/browser prefers-color-scheme media query
 */
function resolveTheme(): ResolvedTheme {
  const htmlTheme = document.documentElement.getAttribute('data-theme');

  if (htmlTheme === THEME.DARK) return 'banenor-dark';
  if (htmlTheme === THEME.LIGHT) return 'banenor-light';

  try {
    const saved = localStorage.getItem('theme');
    if (saved === THEME.DARK) return 'banenor-dark';
    if (saved === THEME.LIGHT) return 'banenor-light';
  } catch {
    // localStorage not available (private browsing, etc.)
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'banenor-dark'
    : 'banenor-light';
}

export interface UseThemeResult {
  resolvedTheme: ResolvedTheme;
  isDark: boolean;
}

export function useTheme(): UseThemeResult {
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(resolveTheme);

  useEffect(() => {

    const observer = new MutationObserver(() => {
      setResolvedTheme(resolveTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemChange = () => {
      const htmlTheme = document.documentElement.getAttribute('data-theme');
      if (!htmlTheme || htmlTheme === THEME.SYSTEM) {
        setResolvedTheme(resolveTheme());
      }
    };

    mediaQuery.addEventListener('change', handleSystemChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', handleSystemChange);
    };
  }, []);

  return { resolvedTheme, isDark: resolvedTheme === 'banenor-dark' };
}
